/**
 * KAN-834: блок "Итог" карточки клиента в приложении менеджера.
 * Те же действия, что у строки "Требуют внимания" (useAttentionActions,
 * useAttentionDone, useAttentionSuccess), с тостом после сохранения:
 *   - KAN-852: "Взять в работу" и "Назначить" вместо "Дозвонился" (useMobileWork,
 *     действия по роли и правам; чужого клиента обычный менеджер не берёт);
 *   - Перезвонить: дата и время по времени организации (KAN-840) -> PATCH crm/clients next_contact_at,
 *     затем строка внимания закрывается: у клиента с менеджером менеджер сохраняется
 *     (перезвон не переназначает чужого клиента старшему), ничьего клиента основное
 *     действие взятия в работу (себе или окно назначения);
 *   - Отказ: после подтверждения исход refused;
 *   - Успех: оформление заказа клиента или "Пропустить" (KAN-903, useAttentionSuccess);
 *   - Комментарий: PATCH crm/clients comment.
 * KAN-940: комментарий сохраняется сам (useAutosaveText в блоке): saveCommentQuiet
 * без тоста и без состояния хука (вызывается и после ухода с экрана), отдаёт
 * текст ошибки или null.
 * KAN-911: "Отработано" (Успех / Не успех) только у взятого в работу
 * (canMarkOutcome по client.taken_at с backend).
 * После итога карточка клиента перечитывается (этап и следующий контакт).
 * KAN-915: и списки "Мои лиды": лид с перезвоном уходит во вкладку "Следующая связь".
 * KAN-955: "Работа идёт" у клиента с причиной "Нет движения" (строка внимания клиента).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { attentionRowRemover, useAttentionActions } from '@/components/dashboard/useAttentionActions';
import { useAttentionDone, type RunAttentionAction } from '@/components/dashboard/useAttentionDone';
import { useAttentionSuccess } from '@/components/dashboard/useAttentionSuccess';
import { useCrmRowEdit } from '@/hooks/useCrmRowEdit';
import { useMobileWork } from '@/hooks/useMobileWork';
import { MOBILE_ATTENTION_QUERY_KEY, useClientAttentionItem } from '@/hooks/useMobileData';
import { clientQueryKey } from '@/services/api/clientQueries';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { isoToZonedInput, orgTimezoneLabel, zonedInputToIso } from '@/lib/orgTimezone';
import { attentionItemFromClient, OUTCOME_SAVED_TEXT } from '@/lib/mobileClient';
import { MOBILE_LEADS_QUERY_ROOT } from '@/lib/mobileLeadsTabs';
import { isTakenToWork } from '@/lib/takenToWork';
import { isIdleRow, WORK_CONTINUES } from '@/lib/workContinues';
import type { Client } from '@/types/client';

export const CALLBACK_TIME_REQUIRED = 'Укажите дату и время перезвона';
export const CALLBACK_SAVED = 'Перезвон запланирован';

export function useMobileOutcome(client: Client) {
  const queryClient = useQueryClient();
  const actions = useAttentionActions(MOBILE_ATTENTION_QUERY_KEY, attentionRowRemover);
  const { save } = useCrmRowEdit();
  const clientId = client.id;
  const item = useMemo(() => attentionItemFromClient(client), [client]);
  const timeZone = useOrgTimezone();
  // KAN-955: строка внимания клиента (общий кеш со вкладкой "Внимание"): причина idle даёт "Работа идёт".
  const { data: attentionItem } = useClientAttentionItem(clientId);

  const refreshClient = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: clientQueryKey(clientId) });
    void queryClient.invalidateQueries({ queryKey: MOBILE_LEADS_QUERY_ROOT });
  }, [queryClient, clientId]);

  const { run: runAction } = actions;
  const run: RunAttentionAction = useCallback(
    (id, action, managerId, message) => runAction(id, action, managerId, message ?? OUTCOME_SAVED_TEXT[action]),
    [runAction]
  );
  const done = useAttentionDone(run, 'app');
  const work = useMobileWork(item, done);
  const success = useAttentionSuccess(run);

  // Запрос итога закончился: карточка перечитывается.
  const pending = actions.isPending(clientId);
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !pending) refreshClient();
    wasPending.current = pending;
  }, [pending, refreshClient]);

  const [refuseAsked, setRefuseAsked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const patch = async (body: Parameters<typeof save>[1], okText: string): Promise<boolean> => {
    setSaving(true);
    setError(null);
    const failed = await save(clientId, body);
    setSaving(false);
    if (failed) {
      setError(failed);
      return false;
    }
    refreshClient();
    toast.success(okText);
    return true;
  };

  return {
    /** KAN-840: подпись поля перезвона и начальное значение по времени организации. */
    callbackLabel: `Когда перезвонить, ${orgTimezoneLabel(timeZone)}`,
    callbackInitial: isoToZonedInput(client.followup_at, timeZone),
    // KAN-869: передача клиента тоже блокирует кнопки итога; KAN-903: и создание корзины.
    pending: pending || work.busy || success.pending,
    saving,
    error,
    done,
    success,
    refuseAsked,
    work,
    /** KAN-911: клиент взят в работу: доступно "Отработано". */
    canMarkOutcome: isTakenToWork(client),
    /** KAN-955: у клиента причина "Нет движения": доступно "Работа идёт". */
    canContinueWork: isIdleRow(attentionItem),
    continueWork: () => run(clientId, WORK_CONTINUES),
    markSuccess: () => success.request(item),
    askRefuse: () => setRefuseAsked(true),
    cancelRefuse: () => setRefuseAsked(false),
    confirmRefuse: () => {
      setRefuseAsked(false);
      run(clientId, 'refused');
    },
    scheduleCallback: async (input: string) => {
      const iso = zonedInputToIso(input, timeZone);
      if (!iso) {
        setError(CALLBACK_TIME_REQUIRED);
        return;
      }
      if (!(await patch({ next_contact_at: iso }, CALLBACK_SAVED))) return;
      // KAN-852: перезвон не меняет менеджера клиента; ничьего берёт основное действие.
      if (item.manager) run(clientId, 'done', item.manager.id);
      else done.request(item);
    },
    /** KAN-940: тихое сохранение комментария для автосохранения: текст ошибки или null. */
    saveCommentQuiet: async (comment: string): Promise<string | null> => {
      const failed = await save(clientId, { comment });
      if (!failed) refreshClient();
      return failed;
    },
  };
}

export type MobileOutcome = ReturnType<typeof useMobileOutcome>;
